import { addMinutes, isBefore, isEqual } from 'date-fns';
import { capitalize } from '@mui/material';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import {
  Appointments,
  AppointmentStatus,
  AppointmentType,
  Payments,
  ReferralSources,
} from '@prisma/client';
import { NavigateFunction } from 'react-router-dom';
import {
  Appointment,
  LoadedUser,
  Option,
  Section,
  Service,
} from '../../interfaces';
import { isEndDateAcrossDateline } from '../../utils/global/helper';
import {
  DEFAULT_LENGTH_OPTIONS,
  DEFAULT_TIMEZONE,
  FIFTEEN_MIN_LENGTH_OPTIONS,
  LENGTH_OPTIONS,
  USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK,
} from '../../constants/appointments';
import {
  AdditionalAppointment,
  CreateAptValues,
} from '../../utils/form/appointmentForm';
import {
  getPaymentsTipAmount,
  getPaymentsTotalDiscountedAmount,
} from '../payments';
import { Payment } from '../../types/reports';
import { GetWaiversResponse } from '../../types/waivers';
import { AcceptFormState, TakenSessionSlot } from '../../types/appointments';
import {
  ALLOWED_VOID_PAYMENT_METHODS,
  CREDIT_PAYMENT_METHODS,
} from '../../constants/payments';
import type { createAppointmentChat } from '../../services/appointments';
import { ROUTES } from '../../constants/global';

export function isAppointmentDepositPaid(
  appointment:
    | Pick<
        Appointment | Appointments,
        'depositPrice' | 'confirmedOn' | 'depositPaidOn' | 'paymentStatus'
      >
    | null
    | undefined,
) {
  return (
    !!appointment?.depositPrice &&
    (!!appointment?.confirmedOn || !!appointment?.depositPaidOn) &&
    appointment?.paymentStatus === 'SUCCESS'
  );
}

export function getAppointmentTipAmount(
  appointmentPayments: (Payments | Payment)[],
) {
  return getPaymentsTipAmount(appointmentPayments);
}

export function getAppointmentTotalDiscountedAmount(
  appointmentPayments: Payments[],
) {
  return getPaymentsTotalDiscountedAmount(
    appointmentPayments?.filter(payment => payment.status === 'SUCCESS'),
  );
}

export function doesCashAppointmentHaveTransaction(
  appointment: Appointment,
  payments: Payments[],
) {
  return payments.some(
    payment =>
      payment.appointmentId === appointment.appointmentId &&
      payment.method === 'CASH',
  );
}

export function filterCashAppointmentsWithoutTransactions(
  appointments: Appointment[],
  payments: Payments[],
) {
  return appointments.filter(
    appointment => !doesCashAppointmentHaveTransaction(appointment, payments),
  );
}

export function getCrossDatelineError({
  startDateTime,
  endDateTime,
  type = 'appointment',
  appointment,
  timezone,
  additionalAppointments,
}: {
  startDateTime: string | Date;
  endDateTime: string | Date;
  timezone: string;
  type?: 'appointment' | 'timeblock';
  appointment?: Appointment | null;
  additionalAppointments?: AdditionalAppointment[];
}) {
  const label = type === 'appointment' ? 'appointment' : 'time block';
  const pluralLabel = `${label}s`;

  const isTimeChanged =
    (type === 'appointment' && !!additionalAppointments?.length) ||
    !appointment?.startDateTime ||
    !appointment?.endDateTime ||
    !isEqual(new Date(appointment.startDateTime), new Date(startDateTime)) ||
    !isEqual(new Date(appointment.endDateTime), new Date(endDateTime));

  const timezoneToCompare = USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK
    ? DEFAULT_TIMEZONE
    : timezone;

  const appointmentsThatCross = [
    {
      startDateTime,
      endDateTime,
    },
    ...(additionalAppointments || []),
  ]
    .map(appt => {
      const start = new Date(appt.startDateTime);
      const end = new Date(appt.endDateTime);

      // Convert the start time to Pacific time to find the correct cutoff
      const startInDefaultTZ = formatInTimeZone(
        start,
        timezoneToCompare,
        'yyyy-MM-dd',
      );
      const elevenThirtyPMDefaultTZ = zonedTimeToUtc(
        `${startInDefaultTZ}T23:30:00`,
        timezoneToCompare,
      );

      const startDay = formatInTimeZone(start, timezone, 'MMM d');
      const endTime = formatInTimeZone(
        elevenThirtyPMDefaultTZ,
        timezone,
        'h:mm a',
      );

      return {
        start,
        end,
        startDay,
        endTime,
      };
    })
    .filter(appt => isEndDateAcrossDateline(appt.start, appt.end, timezone));

  if (isTimeChanged && appointmentsThatCross.length) {
    const isTzDefault = timezone === timezoneToCompare;
    const isMultiple = appointmentsThatCross.length > 1;
    const first = appointmentsThatCross[0];
    const title = `You've selected an end time of ${formatInTimeZone(
      first.end,
      timezone,
      'h:mm a',
    )}`;
    const message = isMultiple
      ? `These ${appointmentsThatCross.length} ${pluralLabel} will be scheduled to end at ${first.endTime} if you continue.`
      : `This ${label} will be scheduled from ${formatInTimeZone(
          first.start,
          timezone,
          'MMM d h:mm a',
        )} to ${first.startDay} ${first.endTime} if you continue.`;
    return {
      title,
      message: `${capitalize(pluralLabel)} that go past midnight${
        USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK && !isTzDefault
          ? ` in ${DEFAULT_TIMEZONE} `
          : ''
      } can not be scheduled. Are you sure you want to continue? ${message}`,
    };
  }

  return null;
}

export function validatePrice(
  values: CreateAptValues | AcceptFormState,
  appointmentType: AppointmentType,
) {
  if (appointmentType === 'CONSULTATION' || appointmentType === 'WALK_IN')
    return null;

  const { priceType, minPrice, maxPrice } = values;

  const price =
    'price' in values
      ? values.price
      : 'setPrice' in values
      ? values.setPrice
      : null;
  const depositPrice =
    'depositPrice' in values
      ? values.depositPrice
      : 'depositAmount' in values
      ? values.depositAmount
      : null;

  if (priceType === 'FIXED' && depositPrice && depositPrice > (price ?? 0)) {
    return {
      error:
        'Deposit amount cannot be greater than price. The price of the appointment should include the deposit price.',
    };
  }

  if (priceType === 'RANGE' && (minPrice ?? 0) > (maxPrice ?? 0)) {
    return {
      error: 'Minimum price cannot be greater than maximum price.',
    };
  }
  return null;
}

export function getWalkInStartDateTime() {
  return addMinutes(new Date(), 2);
}

export function getAppointmentFormErrorMessage(error: Error) {
  if (error.message.startsWith('All stations at the shop are already booked')) {
    return error.message;
  }
  return error.message;
}

export function getCalendarTimezone(
  user: LoadedUser | undefined,
  businessTripId?: string,
) {
  return (
    user?.business.businessTrips.find(t => t.tripId === businessTripId)
      ?.timezone ||
    user?.timezone ||
    DEFAULT_TIMEZONE
  );
}

export function isAppointmentUpcoming(
  appointment: Pick<Appointments, 'endDateTime'>,
) {
  return (
    !!appointment.endDateTime &&
    isBefore(new Date(), new Date(appointment.endDateTime))
  );
}

export function isAppointmentInPast(
  appointment: Pick<Appointments, 'endDateTime'>,
) {
  return (
    !!appointment.endDateTime &&
    isBefore(new Date(appointment.endDateTime), new Date())
  );
}

export function isAppointmentCompleted(
  appointment: Pick<Appointments, 'checkoutSessionCompleted' | 'wasNoShow'>,
) {
  return appointment.checkoutSessionCompleted || appointment.wasNoShow;
}

export function isAppointmentPending(
  appointment: Pick<Appointments, 'status'>,
) {
  return (
    appointment.status === 'REQUESTED' || appointment.status === 'ACCEPTED'
  );
}

export function getServiceName(
  appointment: Appointment,
  loadedUser: LoadedUser | null,
) {
  const isTattoo = appointment.serviceCategory === 'TATTOO';

  if (isTattoo) return 'Tattoo Appointment';

  const service = loadedUser?.business.services?.find(
    tempService => tempService.serviceId === appointment.serviceId,
  );

  return service?.serviceName || '';
}

function getDurationLabel(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} hour${
      hours > 1 ? 's' : ''
    } and ${remainingMinutes} minutes`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  return `${remainingMinutes} minutes`;
}

export function getServiceLengthOptions(service: Service) {
  const defaultDuration = service.defaultDurationInMinutes;
  const durations = [...DEFAULT_LENGTH_OPTIONS];

  if (!defaultDuration) return durations;

  const exists = durations.some(duration => duration.value === defaultDuration);

  if (exists) return durations;

  const defaultDurationLabel = getDurationLabel(defaultDuration);
  const defaultDurationObject = {
    label: defaultDurationLabel,
    value: defaultDuration,
  };

  durations.push(defaultDurationObject);
  durations.sort((a, b) => a.value - b.value);

  return durations;
}

export function getAppointmentStatus(appointment: {
  status: AppointmentStatus | null | undefined;
  previousStatus: AppointmentStatus | null | undefined;
}) {
  return appointment?.status === 'ARCHIVED'
    ? appointment?.previousStatus || 'ARCHIVED'
    : appointment?.status;
}

export function isWaiverSelectable({
  waiverData,
  hasWaiversFeature,
  appointmentType,
}: {
  waiverData: GetWaiversResponse | undefined;
  hasWaiversFeature: boolean;
  appointmentType: AppointmentType | undefined;
}) {
  return (
    !!waiverData?.published?.enabled &&
    hasWaiversFeature &&
    appointmentType !== 'CONSULTATION'
  );
}

export function getDepositPlacementSelectInput({
  name = 'depositPlacement',
  disabled = false,
  required = false,
}: {
  name?: string;
  disabled?: boolean;
  required?: boolean;
} = {}) {
  return {
    label: `Deposit Handling${required ? ' *' : ''}`,
    type: 'select',
    options: [
      {
        value: 'FIRST',
        label: 'Collect & apply deposit to the first appointment',
      },
      {
        value: 'LAST',
        label: 'Collect & apply deposit to the last appointment',
      },
    ],
    placeholder: 'How do you want to apply the deposit for these appointments?',
    name,
    disabled,
  };
}

export const getTakenSessionSlots = (
  index: number,
  values: CreateAptValues,
) => {
  // if index is -1, return all of the `additionalAppointments`
  // otherwise, return the original appointment and the `additionalAppointments` excluding the one at index
  const additionalAppointments: TakenSessionSlot[] = [
    ...(values.additionalAppointments || []),
  ].filter(s => s.startDateTime && s.appointmentLengthInMinutes);
  if (index === -1) return additionalAppointments;
  additionalAppointments.splice(index, 1);
  if (values.startDateTime && values.appointmentLengthInMinutes) {
    const startDateTime = new Date(values.startDateTime);
    const appointmentLengthInMinutes = parseInt(
      values.appointmentLengthInMinutes,
      10,
    );
    if (appointmentLengthInMinutes) {
      const endDateTime = addMinutes(startDateTime, appointmentLengthInMinutes);
      additionalAppointments.push({
        startDateTime: startDateTime.toISOString(),
        endDateTime: endDateTime.toISOString(),
      });
    }
  }
  return additionalAppointments.map(slot => ({
    startDateTime: new Date(slot.startDateTime).toISOString(),
    endDateTime: new Date(slot.endDateTime).toISOString(),
  }));
};

export const isDepositPlacementRequired = (
  additionalAppointments: AdditionalAppointment[],
  depositPrice: number | undefined,
) => !!additionalAppointments?.length && (depositPrice ?? 0) > 0;

export const getCancelAppointmentDepositOptions = (
  appointment: Appointment | Appointments,
): Required<Option<string>>[] => {
  const isRefundable =
    !!appointment.depositPaymentMethod &&
    CREDIT_PAYMENT_METHODS.includes(appointment.depositPaymentMethod);
  const isVoidable =
    !!appointment.depositPaymentMethod &&
    ALLOWED_VOID_PAYMENT_METHODS.includes(appointment.depositPaymentMethod);

  const options = [
    {
      label: 'Credit the deposit to the clients account',
      value: 'UNUSED',
    },
    {
      label: 'Forfeit the deposit',
      value: 'FORFEITED',
    },
  ];

  const refundOption = {
    value: isRefundable ? 'REFUNDED' : isVoidable ? 'VOIDED' : '',
    label: isRefundable
      ? 'Refund the deposit'
      : isVoidable
      ? 'Void the deposit'
      : '',
  };

  if (refundOption.value) {
    options.splice(1, 0, refundOption);
  }

  return options;
};

export function navigateToChat(
  navigate: NavigateFunction,
  chatResponse: Awaited<ReturnType<typeof createAppointmentChat>>,
) {
  const { conversation, appointmentId, generalRequestId } = chatResponse;
  const searchParams = new URLSearchParams();
  if (appointmentId) searchParams.set('appointment', appointmentId);
  if (generalRequestId) searchParams.set('request', generalRequestId);
  const query = searchParams.toString();
  navigate(`${ROUTES.chat}/${conversation.id}${query ? `?${query}` : ''}`);
}

export function getLengthOptions({
  user,
  appointmentType,
  serviceId,
}: {
  user: LoadedUser | null;
  appointmentType?: AppointmentType;
  serviceId?: number;
}) {
  const isConsultation = appointmentType === 'CONSULTATION';

  const service = serviceId
    ? user?.business.services?.find(s => s.serviceId === serviceId)
    : null;

  const useFifteenMinLengthOptions =
    user?.business.id === 'cm51byiny0043ntqghi8j4rwm' ||
    user?.business.id === 'cm4jjdgzb005yiaanc6quzb32' ||
    user?.userId === 'cljego2px0004s60umrsops0s';

  const newAppointmentLengthOptions = useFifteenMinLengthOptions
    ? FIFTEEN_MIN_LENGTH_OPTIONS
    : LENGTH_OPTIONS.NEW_APPOINTMENT;

  const appointmentLengthOptions = isConsultation
    ? LENGTH_OPTIONS.CONSULTATION
    : service
    ? getServiceLengthOptions(service)
    : newAppointmentLengthOptions;

  return appointmentLengthOptions;
}

export function createReferralSourceSection(
  referralSource: ReferralSources | null,
): Section | null {
  if (!referralSource) return null;

  const rows = [
    {
      type: 'TEXT',
      label: 'Source',
      value: [referralSource.type || ''],
    },
  ];

  if (referralSource.details) {
    rows.push({
      type: 'TEXT',
      label: 'Details',
      value: [referralSource.details || ''],
    });
  }

  return {
    name: 'Referral Source',
    rows,
  } as Section;
}
